/** @format */

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AsyncAutocomplete from '../components/common/AsyncAutocomplete';
import DocumentTitle from '../components/common/DocumentTitle';
import LoadingDialog from '../components/common/LoadingDialog';
import { TextInput } from '../components/form/Inputs';
import { usePrerequisites } from '../contexts/Prerequisites';
import { useDidMount, useSetState, useUniqueEffect } from '../hooks';
import SterisLogoPNG from '../styles/img/STERIS-Logo.png';
import util from '../util/index';

const formSX = {
	'& .MuiInputLabel-asterisk': { color: 'error.main', fontWeight: 'bold' }
};
const LoginForm = (props) => {
	const [{ strings, language, session, shouldChooseClient, Clients, lastLoginClientId }, { loginUser }] = usePrerequisites();
	const { loggedin } = useSelector((storeState) => storeState.auth);
	const [clients, defaultClientId] = useMemo(() => {
		let opts = [];
		if (Array.isArray(Clients)) {
			opts = Clients.map(({ ClientName, LicenseClientId }) => ({
				id: LicenseClientId,
				label: ClientName
			}));
		}
		const opt = opts.find((client) => client.id === lastLoginClientId)?.id || opts[0]?.id;
		return [opts, opt];
	}, [Clients]);
	const [state, setState] = useSetState({
		loading: false,
		loadingMessage: 'Loading...',
		readOnly: false,
		clientId: defaultClientId,
		theme: util.localData.PTXPreferredTheme || 'blue',
		docTitle: 'STERIS | Login'
	});
	useDidMount(() => {
		delete util.CloseNotification;
	});

	useUniqueEffect(() => {
		util.localData.PTXPreferredLang = language;
	}, [language]);

	useUniqueEffect(() => {
		if (!loggedin) {
			setState({ clientId: null });
		}
	}, [loggedin]);

	useUniqueEffect(() => {
		if (shouldChooseClient) {
			setState({ clientId: defaultClientId });
		}
	}, [shouldChooseClient]);

	const handleOnSubmit = (e) => {
		e.preventDefault();
		loginUser({
			username: state.username,
			password: state.password,
			...(state.clientId ? { clientId: state.clientId } : {}),
			scopeId: '',
			defaultLanguage: language,
			rememberMe: false
		});
	};

	const handleClientChange = (e, newValue = {}) => {
		setState({ clientId: (newValue || {}).id }); // handling when the newValue is null
	};

	const handleOnInputChange = (e) => {
		const field = e.target.name;
		setState({ [field]: e.target.value });
	};

	const { loading, invalidLicense, UpdateLogo, hasError } = state;


	const parseData = { HospitalName: 'ConnectAssure Technology', HospitalLogo: SterisLogoPNG, ...session?.tags };
	const hospitalName = parseData.HospitalName;
	const hospitalLogo = parseData.HospitalLogo;
	const clientId = parseData.ClientId;

	return (
		<div>
			{/* <DocumentTitle title={docTitle ? docTitle : ''}></DocumentTitle> */}
			{!invalidLicense && !hasError && (
				<div className="wrapper wrapper-content">
					<Paper
						sx={{
							justifyContent: "center",
							margin: "auto",
							maxWidth: "500px",
							display: "flex",
							flexDirection: "column",
							padding: "20px",
							alignItems: "center"
						}}
					>
						<img
							src={UpdateLogo ? hospitalLogo : SterisLogoPNG}
							alt="logo"
						/>
						<br />
						<h3>
							{strings.WelcomeTo} {hospitalName}
						</h3>
						<Avatar>
							<LockOutlinedIcon />
						</Avatar>
						<Typography
							component="h1"
							variant="h5"
						>
							{strings.Login}
						</Typography>
						<Box
							component="form"
							sx={formSX}
							onSubmit={handleOnSubmit}
							style={{ width: "100%" }}
						>
							<div width="100%">
								<Grid
									p={1}
									xs={12}
								>
									<TextInput
										p={1}
										name="username"
										maxLength="50"
										label={strings.Username}
										onChange={handleOnInputChange}
										value={state.username}
										eager
										fullWidth
										autoFocus
										required
										disabled={shouldChooseClient}
									/>
								</Grid>
								<Grid
									item
									p={1}
								>
									<TextInput
										name="password"
										maxLength="50"
										label={strings.Password}
										onChange={handleOnInputChange}
										fullWidth
										eager
										type="password"
										value={state.password}
										required
										autoComplete="current-password"
										disabled={shouldChooseClient}
									/>
								</Grid>
								{shouldChooseClient && (
									<Grid
										item
										p={1}
									>
										<AsyncAutocomplete
											name="clientId"
											size="small"
											margin="dense"
											label={strings.Client}
											options={clients}
											value={state.clientId}
											onChange={handleClientChange}
										/>
									</Grid>
								)}

								<Grid
									item
									p={1}
								>
									<Button
										type="submit"
										fullWidth
										variant="contained"
										color="primary"
									>
										{strings.Login}
									</Button>
								</Grid>
								{!shouldChooseClient && <Grid
									item
									p={1}
									display={"flex"}
									justifyContent={"center"}
								>
									<Button
										component={Link}
										size="small"
										variant="text"
										to="/forgotPassword"
									>
										{strings.ForgotPassword}?
									</Button>
								</Grid>}
							</div>
						</Box>
						<Box mt={5}>
							{strings.Copyright} &copy; STERIS Corporation {new Date().getFullYear()}
						</Box>
					</Paper>
				</div>
			)}
		</div>
	);
};

export default LoginForm;
